import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { EmptyState, StatusPill, TablePagination } from '../ui';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const formatTs = (timestamp) => {
  if (!timestamp) return '—';
  try {
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'Asia/Karachi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return timestamp;
  }
};

const statusPillClass = (code) => {
  if (code >= 500) {
    return 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  if (code >= 400) {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  }
  return undefined;
};

const methodClass = (method) => {
  const m = (method || '').toUpperCase();
  if (m === 'GET') return 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
  if (m === 'POST') return 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  if (m === 'PUT' || m === 'PATCH') {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  }
  if (m === 'DELETE') return 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300';
};

const CodeBlock = ({ label, value }) => {
  if (!value) return null;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">{label}</p>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(text)}
          className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
      <pre className="text-xs font-mono bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 rounded-lg p-3 overflow-x-auto max-h-64 whitespace-pre-wrap break-all">
        {text}
      </pre>
    </div>
  );
};

const ErrorLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    status_class: '',
    method: '',
    search: '',
    severity: '',
    user_email: '',
  });
  const [draftSearch, setDraftSearch] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/monitoring/error-logs/stats', {
        headers: authHeaders(),
      });
      setStats(res.data);
    } catch {
      /* non-critical */
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page,
        limit,
        ...(filters.status_class ? { status_class: filters.status_class } : {}),
        ...(filters.method ? { method: filters.method } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.severity ? { severity: filters.severity } : {}),
        ...(filters.user_email ? { user_email: filters.user_email } : {}),
      };
      const res = await axios.get('/api/monitoring/error-logs', {
        headers: authHeaders(),
        params,
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load error logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setFilters((f) => ({ ...f, search: draftSearch.trim() }));
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-page-${page}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearOld = async () => {
    if (!window.confirm('Delete error logs older than 30 days?')) return;
    try {
      await axios.delete('/api/monitoring/error-logs', {
        headers: authHeaders(),
        params: { older_than_days: 30 },
      });
      fetchLogs();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to clear logs');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total errors', value: stats?.total ?? '—', tone: 'text-secondary-900 dark:text-white' },
          { label: 'Last hour', value: stats?.last_hour ?? '—', tone: 'text-red-600' },
          { label: 'Last 24h', value: stats?.last_24h ?? '—', tone: 'text-amber-600' },
          {
            label: 'Critical (5xx)',
            value: stats?.by_severity?.critical ?? 0,
            tone: 'text-red-700',
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4"
          >
            <p className="text-xs text-secondary-500">{s.label}</p>
            <p className={`text-2xl font-semibold ${s.tone}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
        <form onSubmit={applySearch} className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="flex-1">
            <label className="text-xs text-secondary-500 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                className="input-field pl-9 w-full"
                placeholder="Endpoint, message, correlation ID, email…"
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-secondary-500 mb-1 block">Status</label>
            <select
              className="input-field"
              value={filters.status_class}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, status_class: e.target.value }));
              }}
            >
              <option value="">All</option>
              <option value="4xx">4xx Client</option>
              <option value="5xx">5xx Server</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-secondary-500 mb-1 block">Method</label>
            <select
              className="input-field"
              value={filters.method}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, method: e.target.value }));
              }}
            >
              <option value="">All</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-secondary-500 mb-1 block">Severity</label>
            <select
              className="input-field"
              value={filters.severity}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, severity: e.target.value }));
              }}
            >
              <option value="">All</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary inline-flex items-center gap-1.5">
              <Search className="h-4 w-4" /> Filter
            </button>
            <button
              type="button"
              onClick={() => {
                fetchLogs();
                fetchStats();
              }}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={exportJson} className="btn-secondary inline-flex items-center gap-1.5" title="Export page JSON">
              <Download className="h-4 w-4" />
            </button>
            <button type="button" onClick={clearOld} className="btn-secondary inline-flex items-center gap-1.5 text-red-600" title="Clear logs older than 30 days">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary-500 gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" /> Loading error logs…
          </div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title="No failed API requests"
            description="When APIs return 4xx/5xx, full request details will appear here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-secondary-50 dark:bg-secondary-900/50 text-left text-xs uppercase text-secondary-500">
                <tr>
                  <th className="px-3 py-2.5 w-8" />
                  <th className="px-3 py-2.5">Time</th>
                  <th className="px-3 py-2.5">Method</th>
                  <th className="px-3 py-2.5">Endpoint</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">User</th>
                  <th className="px-3 py-2.5">Duration</th>
                  <th className="px-3 py-2.5">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                {logs.map((log) => {
                  const open = expandedId === log.id;
                  return (
                    <React.Fragment key={log.id || log.correlation_id}>
                      <tr
                        className="hover:bg-secondary-50/80 dark:hover:bg-secondary-700/40 cursor-pointer"
                        onClick={() => setExpandedId(open ? null : log.id)}
                      >
                        <td className="px-3 py-2.5 text-secondary-400">
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-secondary-600 dark:text-secondary-300">
                          {formatTs(log.timestamp)}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${methodClass(log.method)}`}>
                            {log.method}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs max-w-[240px] truncate" title={log.endpoint}>
                          {log.endpoint}
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusPill
                            status={String(log.status_code)}
                            colorClass={statusPillClass(log.status_code)}
                            showDot={false}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-xs truncate max-w-[140px]" title={log.user_email || ''}>
                          {log.user_email || '—'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {log.duration_ms != null ? `${log.duration_ms}ms` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-secondary-600 dark:text-secondary-300 max-w-[220px] truncate" title={log.error_message || ''}>
                          {log.error_message || '—'}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-secondary-50/50 dark:bg-secondary-900/30">
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div className="space-y-2">
                                <p>
                                  <span className="text-secondary-500">Correlation ID: </span>
                                  <span className="font-mono text-xs">{log.correlation_id}</span>
                                  <button
                                    type="button"
                                    className="ml-2 text-primary-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard?.writeText(log.correlation_id || '');
                                    }}
                                  >
                                    <Copy className="h-3.5 w-3.5 inline" />
                                  </button>
                                </p>
                                <p>
                                  <span className="text-secondary-500">Severity: </span>
                                  <span className="capitalize">{log.severity || '—'}</span>
                                </p>
                                <p>
                                  <span className="text-secondary-500">Error type: </span>
                                  {log.error_type || '—'}
                                </p>
                                <p>
                                  <span className="text-secondary-500">Client IP: </span>
                                  {log.client_ip || '—'}
                                </p>
                                <p>
                                  <span className="text-secondary-500">User agent: </span>
                                  <span className="text-xs break-all">{log.user_agent || '—'}</span>
                                </p>
                                <p>
                                  <span className="text-secondary-500">Full URL: </span>
                                  <span className="font-mono text-xs break-all">{log.full_url || '—'}</span>
                                </p>
                              </div>
                              <div>
                                {log.query_params && Object.keys(log.query_params).length > 0 && (
                                  <CodeBlock label="Query params" value={log.query_params} />
                                )}
                                <CodeBlock label="Request headers" value={log.request_headers} />
                              </div>
                            </div>
                            <CodeBlock label="Request body" value={log.request_body} />
                            <CodeBlock label="Response / error body" value={log.response_body} />
                            <CodeBlock label="Stack trace" value={log.stack_trace} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 border-t border-secondary-100 dark:border-secondary-700">
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={limit}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setLimit(size);
              setPage(1);
            }}
            itemLabel="errors"
          />
        </div>
      </div>

      {/* Top failing endpoints */}
      {stats?.by_endpoint?.length > 0 && (
        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
            Top failing endpoints
          </h3>
          <div className="space-y-2">
            {stats.by_endpoint.slice(0, 8).map((row) => (
              <div
                key={`${row.method}-${row.endpoint}`}
                className="flex items-center justify-between text-sm gap-3"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${methodClass(row.method)}`}>
                    {row.method}
                  </span>
                  <span className="font-mono text-xs truncate">{row.endpoint}</span>
                </div>
                <span className="text-red-600 font-medium flex-shrink-0">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLogsTab;
